import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  CalendarDays,
  CheckCircle2,
  Clock,
  Filter,
  XCircle,
} from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { useEmployees, useLeaves } from '../../hooks/useHR';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import EmptyState from '../../components/ui/EmptyState';
import Input from '../../components/ui/Input';
import MetricCard from '../../components/ui/MetricCard';
import Modal from '../../components/ui/Modal';
import Surface from '../../components/ui/Surface';
import type { Employee, Leave } from '../../types/firestore';
import type { DataTableColumn } from '../../types/ui';

type LeaveAction = {
  leave: Leave;
  mode: 'approve' | 'reject';
};

const leaveTypeLabels: Record<Leave['type'], string> = {
  annual: 'سنوية',
  sick: 'مرضية',
  maternity: 'أمومة',
  paternity: 'أبوة',
  emergency: 'طارئة',
  unpaid: 'بدون راتب',
  study: 'دراسية',
  hajj: 'حج',
};

const statusLabels: Record<Leave['status'], string> = {
  pending: 'معلقة',
  approved: 'معتمدة',
  rejected: 'مرفوضة',
  cancelled: 'ملغاة',
};

const statusClasses: Record<Leave['status'], string> = {
  pending:
    'bg-warning-50 text-warning-700 ring-warning-200 dark:bg-warning-950/40 dark:text-warning-300 dark:ring-warning-800',
  approved:
    'bg-success-50 text-success-700 ring-success-200 dark:bg-success-950/40 dark:text-success-300 dark:ring-success-800',
  rejected:
    'bg-danger-50 text-danger-700 ring-danger-200 dark:bg-danger-950/40 dark:text-danger-300 dark:ring-danger-800',
  cancelled:
    'bg-secondary-100 text-secondary-700 ring-secondary-200 dark:bg-secondary-800 dark:text-secondary-300 dark:ring-secondary-700',
};

function toDate(timestamp?: Timestamp) {
  return timestamp?.toDate ? timestamp.toDate() : undefined;
}

function formatDate(timestamp?: Timestamp) {
  const date = toDate(timestamp);
  if (!date) return '-';
  return new Intl.DateTimeFormat('ar-EG', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

const LeavesManagement: React.FC = () => {
  const { employees, fetchEmployees } = useEmployees();
  const {
    organizationLeaves,
    loading,
    fetchOrganizationLeaves,
    approveLeave,
    rejectLeave,
  } = useLeaves();
  const [statusFilter, setStatusFilter] = useState<'all' | Leave['status']>('all');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [action, setAction] = useState<LeaveAction | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchEmployees();
    fetchOrganizationLeaves();
  }, [fetchEmployees, fetchOrganizationLeaves]);

  const employeeMap = useMemo(() => {
    return new Map(employees.map(employee => [employee.id, employee]));
  }, [employees]);

  const departments = useMemo(() => {
    return Array.from(new Set(employees.map(employee => employee.department))).sort();
  }, [employees]);

  const filteredLeaves = useMemo(() => {
    return organizationLeaves.filter(leave => {
      const employee = employeeMap.get(leave.employeeId);
      if (statusFilter !== 'all' && leave.status !== statusFilter) return false;
      if (employeeFilter !== 'all' && leave.employeeId !== employeeFilter) return false;
      if (
        departmentFilter !== 'all' &&
        employee?.department !== departmentFilter
      ) {
        return false;
      }
      return true;
    });
  }, [departmentFilter, employeeFilter, employeeMap, organizationLeaves, statusFilter]);

  const stats = useMemo(() => {
    return {
      total: organizationLeaves.length,
      pending: organizationLeaves.filter(leave => leave.status === 'pending').length,
      approved: organizationLeaves.filter(leave => leave.status === 'approved').length,
      rejected: organizationLeaves.filter(leave => leave.status === 'rejected').length,
      approvedDays: organizationLeaves
        .filter(leave => leave.status === 'approved')
        .reduce((total, leave) => total + (leave.totalDays || 0), 0),
    };
  }, [organizationLeaves]);

  const openAction = (leave: Leave, mode: LeaveAction['mode']) => {
    setAction({ leave, mode });
    setNotes('');
  };

  const closeAction = () => {
    if (submitting) return;
    setAction(null);
    setNotes('');
  };

  const handleAction = async () => {
    if (!action?.leave.id) return;
    setSubmitting(true);
    try {
      if (action.mode === 'approve') {
        await approveLeave(action.leave.id, notes.trim() || undefined);
        toast.success('تم اعتماد طلب الإجازة');
      } else {
        await rejectLeave(action.leave.id, notes.trim() || undefined);
        toast.success('تم رفض طلب الإجازة');
      }
      closeAction();
    } catch (error) {
      toast.error('تعذر تحديث طلب الإجازة');
    } finally {
      setSubmitting(false);
    }
  };

  const columns: DataTableColumn<Leave>[] = [
    {
      key: 'employee',
      header: 'الموظف',
      cell: leave => {
        const employee = employeeMap.get(leave.employeeId);
        return (
          <div>
            <p className='font-semibold text-secondary-900 dark:text-white'>
              {employee?.name || leave.employeeId}
            </p>
            <p className='text-xs text-secondary-500 dark:text-secondary-400'>
              {(employee as Employee | undefined)?.position || '-'}
            </p>
          </div>
        );
      },
    },
    {
      key: 'type',
      header: 'النوع',
      cell: leave => leaveTypeLabels[leave.type],
    },
    {
      key: 'period',
      header: 'الفترة',
      cell: leave => (
        <span>
          {formatDate(leave.startDate)} - {formatDate(leave.endDate)}
        </span>
      ),
      hideOnMobile: true,
    },
    {
      key: 'days',
      header: 'الأيام',
      cell: leave => `${leave.totalDays || 0} يوم`,
    },
    {
      key: 'status',
      header: 'الحالة',
      cell: leave => (
        <span
          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusClasses[leave.status]}`}
        >
          {statusLabels[leave.status]}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'الإجراء',
      cell: leave =>
        leave.status === 'pending' ? (
          <div className='flex flex-wrap gap-2'>
            <Button
              size='sm'
              variant='success'
              leadingIcon={<CheckCircle2 className='h-4 w-4' />}
              onClick={() => openAction(leave, 'approve')}
            >
              اعتماد
            </Button>
            <Button
              size='sm'
              variant='danger'
              leadingIcon={<XCircle className='h-4 w-4' />}
              onClick={() => openAction(leave, 'reject')}
            >
              رفض
            </Button>
          </div>
        ) : (
          <span className='text-xs text-secondary-500 dark:text-secondary-400'>
            تمت المراجعة
          </span>
        ),
    },
  ];

  return (
    <div className='space-y-6'>
      <section className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
        <MetricCard label='إجمالي الطلبات' value={stats.total} icon={CalendarDays} />
        <MetricCard
          label='طلبات معلقة'
          value={stats.pending}
          icon={Clock}
          tone={stats.pending ? 'amber' : 'green'}
        />
        <MetricCard
          label='طلبات معتمدة'
          value={stats.approved}
          icon={CheckCircle2}
          tone='green'
          note={`${stats.approvedDays} يوم معتمد`}
        />
        <MetricCard
          label='طلبات مرفوضة'
          value={stats.rejected}
          icon={XCircle}
          tone={stats.rejected ? 'red' : 'blue'}
        />
      </section>

      <Surface>
        <div className='grid gap-4 md:grid-cols-4'>
          <div className='space-y-1.5'>
            <label className='block text-sm font-semibold text-secondary-700 dark:text-secondary-200'>
              الحالة
            </label>
            <select
              value={statusFilter}
              onChange={event =>
                setStatusFilter(event.target.value as 'all' | Leave['status'])
              }
              className='min-h-11 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm text-secondary-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-secondary-700 dark:bg-secondary-900 dark:text-white'
            >
              <option value='all'>كل الحالات</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className='space-y-1.5'>
            <label className='block text-sm font-semibold text-secondary-700 dark:text-secondary-200'>
              الموظف
            </label>
            <select
              value={employeeFilter}
              onChange={event => setEmployeeFilter(event.target.value)}
              className='min-h-11 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm text-secondary-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-secondary-700 dark:bg-secondary-900 dark:text-white'
            >
              <option value='all'>كل الموظفين</option>
              {employees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
            </select>
          </div>
          <div className='space-y-1.5'>
            <label className='block text-sm font-semibold text-secondary-700 dark:text-secondary-200'>
              القسم
            </label>
            <select
              value={departmentFilter}
              onChange={event => setDepartmentFilter(event.target.value)}
              className='min-h-11 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm text-secondary-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-secondary-700 dark:bg-secondary-900 dark:text-white'
            >
              <option value='all'>كل الأقسام</option>
              {departments.map(department => (
                <option key={department} value={department}>
                  {department}
                </option>
              ))}
            </select>
          </div>
          <div className='flex items-end'>
            <Button
              variant='secondary'
              className='w-full'
              leadingIcon={<Filter className='h-4 w-4' />}
              onClick={() => {
                setStatusFilter('all');
                setEmployeeFilter('all');
                setDepartmentFilter('all');
              }}
            >
              إعادة ضبط
            </Button>
          </div>
        </div>
      </Surface>

      <Surface padding='none'>
        <DataTable
          columns={columns}
          rows={filteredLeaves}
          rowKey={leave => leave.id}
          caption='طلبات الإجازات'
          emptyState={
            <EmptyState
              icon={CalendarDays}
              title={loading ? 'جاري تحميل طلبات الإجازة' : 'لا توجد طلبات إجازة'}
              description='ستظهر طلبات الموظفين هنا بعد تقديمها من لوحة الموظف.'
            />
          }
        />
      </Surface>

      <Modal
        isOpen={Boolean(action)}
        onClose={closeAction}
        title={action?.mode === 'approve' ? 'اعتماد طلب الإجازة' : 'رفض طلب الإجازة'}
        description='يمكن إضافة ملاحظة تظهر في سجل الطلب.'
        footer={
          <>
            <Button variant='secondary' onClick={closeAction} disabled={submitting}>
              إلغاء
            </Button>
            <Button
              variant={action?.mode === 'approve' ? 'success' : 'danger'}
              loading={submitting}
              onClick={handleAction}
            >
              {action?.mode === 'approve' ? 'اعتماد' : 'رفض'}
            </Button>
          </>
        }
      >
        <Input
          label='ملاحظات'
          value={notes}
          disabled={submitting}
          onChange={event => setNotes(event.target.value)}
          placeholder='اختياري'
        />
      </Modal>
    </div>
  );
};

export default LeavesManagement;
